import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  addPolicyVersion,
  editPolicyVersion,
  getPolicyById,
  getPolicyVersionByPolicyIdAndVersion,
  updatePolicyVersionAttachment,
} from 'services/policies-service';
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { VersionFormComponent } from './Components/Form';
import { Main as Container } from 'Components/Main';
import { FormSuccess } from 'Components/Form/Success';
import { useTranslation } from 'react-i18next';
import { DotsLoader } from 'Components/DotsLoader';
import { Dialog, PageHeader } from 'Components/ui';

/* ── Styled Components ── */

const PageWrap = styled.div`
  padding-top: 24px;
  padding-bottom: 32px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  height: 44px;
  background: white;
  color: var(--vo-celeste);
  border: 1.5px solid var(--vo-celeste);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: var(--vo-capri-50); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const SaveBtn = styled.button`
  height: 44px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  &:hover:not(:disabled) { background: var(--vo-cobalto); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

/* ── Component ── */

export const PolicyVersionForm = () => {
  const { t } = useTranslation(['policy']);
  const { id, idVersion } = useParams();
  const navigate = useNavigate();
  const [finished, setFinished] = useState(false);
  const [idPolicyVersion, setIdPolicyVersion] = useState(null);
  const [existingData, setExistingData] = useState(null);

  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      approvedDate: null,
      approver: null,
      author: null,
      policyText: '',
      policyVersionCommentaries: [],
      publishedDate: null,
      reviewedDate: null,
      reviewer: null,
      status: null,
      version: null,
      attachment: null,
    },
  });

  const idemRef = useRef(null);

  const { data: policy } = useQuery(
    ['getPolicyById', id],
    async () => {
      const res = await getPolicyById(id);
      return res.status === 200 ? res.data : null;
    },
    { enabled: !!id }
  );

  const mutation = useMutation(
    async (normalizedPayload) => {
      if (idVersion) {
        return editPolicyVersion(id, idVersion, normalizedPayload);
      }
      if (!idemRef.current) {
        idemRef.current = (window.crypto?.randomUUID?.()) ?? `${Date.now()}-${Math.random()}`;
      }
      return addPolicyVersion(id, { ...normalizedPayload, idempotencyKey: idemRef.current });
    },
    {
      onError: (error) => {
        Dialog.fire({
          title: t('policy:versionForm.swal.errorTitle'),
          html: error?.response?.data?.message || error.message,
          icon: 'error',
        });
      },
      onSuccess: (res) => {
        if (res?.status === 200) {
          setIdPolicyVersion(idVersion);
        } else if (res?.status === 201) {
          setIdPolicyVersion(res?.data?.policyVersionId);
        }
        setFinished(true);
      },
      onSettled: () => {
        idemRef.current = null;
      },
    }
  );

  const handleComplete = async (normalizedPayload) => {
    await mutation.mutateAsync(normalizedPayload);
  };

  const attachmentMutation = useMutation(
    async (newFile) => {
      if (!idVersion) throw new Error('Versão da política não encontrada para atualizar anexo');
      return updatePolicyVersionAttachment(id, idVersion, newFile);
    },
    {
      onError: (error) => {
        Dialog.fire({
          title: t('policy:versionForm.swal.errorTitle'),
          html: error?.response?.data?.message || error.message,
          icon: 'error',
        });
      },
      onSuccess: () => {
        Dialog.alert({
          title: t('policy:versionForm.swal.successTitle'),
          message: t('policy:versionForm.attachedDoc.swapSuccess'),
          icon: 'success',
        }).then(() => window.location.reload());
      },
    }
  );

  const handleUpdateAttachment = async (newFile) => {
    await attachmentMutation.mutateAsync(newFile);
  };

  const handleBack = () => navigate(`/policies/show/${id}`);

  const getPolicyVersion = useCallback(async () => {
    if (id && idVersion) {
      try {
        const resPolicy = await getPolicyVersionByPolicyIdAndVersion(id, idVersion);
        const data = resPolicy.data;
        setExistingData(data);
        Object.keys(data).forEach(key => setValue(key, data[key]));
      } catch (error) {
        Dialog.alert({
          title: t('policy:versionForm.swal.errorTitle'),
          message: error?.response?.data?.message ?? error.message,
          icon: 'error',
        });
      }
    }
  }, [id, idVersion, setValue, t]);

  useEffect(() => {
    getPolicyVersion();
  }, [getPolicyVersion]);

  const isLoading = mutation.isLoading || attachmentMutation.isLoading;

  return (
    <Container>
      <PageWrap>
        {!finished && (
          <>
            <PageHeader
              title={t(idVersion ? 'policy:versions.titleEdit' : 'policy:versions.titleAdd')}
              segments={[
                { label: t('common:breadcrumbs.compliance') },
                { label: t('policy:page.title'), route: '/policies' },
                { label: policy?.name ?? '', route: `/policies/show/${id}` },
                { label: t(idVersion ? 'policy:versions.titleEdit' : 'policy:versions.titleAdd') },
              ]}
              actions={
                <HeaderActions>
                  <CancelBtn type="button" onClick={handleBack} disabled={isLoading}>
                    {t('policy:versionForm.cancel')}
                  </CancelBtn>
                  <SaveBtn form="version-form" type="submit" disabled={isLoading}>
                    {isLoading ? <DotsLoader /> : t('policy:versionForm.save')}
                  </SaveBtn>
                </HeaderActions>
              }
            />
            <VersionFormComponent
              control={control}
              errors={errors}
              getValues={getValues}
              handleBack={handleBack}
              handleComplete={handleComplete}
              handleUpdateAttachment={handleUpdateAttachment}
              handleSubmit={handleSubmit}
              id={id}
              isLoading={isLoading}
              register={register}
              setValue={setValue}
              existingData={existingData}
              policyName={policy?.name}
            />
          </>
        )}
        {mutation.isSuccess && finished && (
          <FormSuccess
            title={t('policy:versions.successMessage')}
            labelStartAgain={t('policy:versions.viewDetails')}
            handleStartAgain={() => navigate(`/policies/show/${id}/versions/${idPolicyVersion}`)}
            showButtonSeeDetail={false}
          />
        )}
      </PageWrap>
    </Container>
  );
};
